<template>
  <div class="flex-1 overflow-auto p-4">
    <div class="flex flex-row w-full">
      <div class="flex-1">
        <woot-loading-state
          v-if="uiFlags.isFetching"
          :message="$t('ROLES.LOADING')"
        />
        <div v-else>
          <p v-if="!rolesList.length">
            {{ $t('ROLES.404') }}
          </p>
          <table v-else class="w-full rounded-[8px] overflow-hidden">
            <thead>
              <tr>
                <th class="min-w-[400px]">
                  <div class="table-header">
                    <span>{{ $t('ROLES.TABLE.HEADER.NAME') }}</span>
                    <div class="relative">
                      <span
                        class="material-symbols-outlined border border-transparent rounded-[4px] hover:bg-neutral-300 cursor-pointer"
                        @click.stop="toggleDropdown('name')"
                      >
                        more_vert
                      </span>
                      <sort-by-dropdown
                        sort-key="name"
                        :visible="activeDropdown === 'name'"
                        :value="sort.key === 'name' ? sort.direction : ''"
                        :asc-text="'A→Z'"
                        :desc-text="'Z→A'"
                        @select="onSort('name', $event)"
                      />
                    </div>
                  </div>
                </th>
                <th class="w-1/2">
                  <div class="table-header">
                    <span>{{ $t('ROLES.TABLE.HEADER.CREATED') }}</span>
                    <div class="relative">
                      <span
                        class="material-symbols-outlined border border-transparent rounded-[4px] hover:bg-neutral-300 cursor-pointer"
                        @click.stop="toggleDropdown('created_at')"
                      >
                        more_vert
                      </span>
                      <sort-by-dropdown
                        sort-key="created_at"
                        :visible="activeDropdown === 'created_at'"
                        :value="sort.key === 'created_at' ? sort.direction : ''"
                        @select="onSort('created_at', $event)"
                      />
                    </div>
                  </div>
                </th>
                <th class="w-1/2">
                  <div class="table-header">
                    <span>{{ $t('ROLES.TABLE.HEADER.UPDATED') }}</span>
                    <div class="relative">
                      <span
                        class="material-symbols-outlined border border-transparent rounded-[4px] hover:bg-neutral-300 cursor-pointer"
                        @click.stop="toggleDropdown('updated_at')"
                      >
                        more_vert
                      </span>
                      <sort-by-dropdown
                        sort-key="updated_at"
                        :visible="activeDropdown === 'updated_at'"
                        :value="sort.key === 'updated_at' ? sort.direction : ''"
                        @select="onSort('updated_at', $event)"
                      />
                    </div>
                  </div>
                </th>
                <th class="min-w-[110px]">
                  <div class="table-header justify-center">
                    <span>{{ $t('ROLES.TABLE.HEADER.ACTIONS') }}</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <tr v-for="(role, index) in rolesList" :key="role.id">
                <td class="font-semibold text-gray-800">
                  <div class="flex flex-row gap-2 items-center">
                    <span
                      v-show="role.is_system_role"
                      class="material-symbols-outlined text-gray-500"
                    >
                      lock
                    </span>
                    <span class="agent-name">
                      {{ $t(`ROLES.NAMES.${role.name}`) }}
                    </span>
                  </div>
                </td>
                <td>
                  <div class="flex flex-row items-center gap-2.5 text-gray-600">
                    <span>{{ role.created_by }}</span>
                    <span class="w-1 h-1 opacity-20 bg-gray-500 rounded-full" />
                    <span>
                      {{ messageStamp(role.created_at, 'dd/MM/yyyy, HH:mm') }}
                    </span>
                  </div>
                </td>
                <td>
                  <div class="flex flex-row items-center gap-2.5 text-gray-600">
                    <span>{{ role.updated_by }}</span>
                    <span class="w-1 h-1 opacity-20 bg-gray-500 rounded-full" />
                    <span>
                      {{ messageStamp(role.updated_at, 'dd/MM/yyyy, HH:mm') }}
                    </span>
                  </div>
                </td>
                <!-- Actions -->
                <td>
                  <div class="flex flex-row gap-1 items-center justify-center">
                    <div
                      v-if="isGeneralManager(role.name)"
                      v-tooltip.top="$t('ROLES.VIEW.BUTTON_TEXT')"
                      class="button-ghost flex flex-row items-center justify-center w-[44px] h-[44px] cursor-pointer"
                      @click="openEditPopup(role)"
                    >
                      <span class="material-symbols-outlined text-gray-500">
                        visibility
                      </span>
                    </div>
                    <div
                      v-if="
                        hasEditPermission('Roles') &&
                        !isGeneralManager(role.name)
                      "
                      v-tooltip.top="$t('ROLES.EDIT.BUTTON_TEXT')"
                      class="button-ghost flex flex-row items-center justify-center w-[44px] h-[44px] cursor-pointer"
                      @click="openEditPopup(role)"
                    >
                      <span class="material-symbols-outlined text-gray-500">
                        edit
                      </span>
                    </div>
                    <button
                      v-if="
                        hasDeletePermission('Roles') && !isGeneralManager(role.name)
                      "
                      v-tooltip.top="$t('ROLES.DELETE.BUTTON_TEXT')"
                      class="button-ghost flex flex-row items-center justify-center w-[44px] h-[44px] cursor-pointer"
                      :is-loading="loading[role.id]"
                      :disabled="role.is_system_role"
                      style="border: 1px solid #e5e7eb"
                      @click="openDeletePopup(role, index)"
                    >
                      <span
                        class="material-symbols-outlined"
                        :class="{
                          'text-[#EF4444]': !role.is_system_role,
                        }"
                      >
                        delete
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <woot-modal :show.sync="showEditPopup" :on-close="hideEditPopup">
      <edit-role
        v-if="showEditPopup"
        :id="currentRole.id"
        :name="currentRole.name"
        :permissions="currentRole.permissions"
        :inbox-permissions="currentRole.inbox_permissions"
        :read-only-name="currentRole.is_system_role"
        :on-close="hideEditPopup"
      />
    </woot-modal>
    <woot-delete-modal
      :show.sync="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('ROLES.DELETE.CONFIRM.TITLE')"
      :message="$t('ROLES.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('ROLES.DELETE.CONFIRM.YES')"
      :reject-text="$t('ROLES.DELETE.CONFIRM.NO')"
    >
      <template v-if="currentRole.assigned_users_count > 0" #additionInfo>
        <div
          class="mx-6 mb-4 p-3 rounded-[4px] bg-[#FFFBEB] border border-[#FEF3C7] flex flex-row gap-2"
        >
          <span class="material-symbols-outlined text-[#F59E0B]">report</span>
          <div>
            <div class="text-[#78350F] mb-1">
              {{
                $t('ROLES.DELETE.ADDITION_INFO.TITLE', {
                  count: currentRole.assigned_users_count,
                })
              }}
            </div>
            <div class="text-[#B45309] text-sm">
              {{ $t('ROLES.DELETE.ADDITION_INFO.DESCRIPTION') }}
            </div>
          </div>
        </div>
      </template>
    </woot-delete-modal>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import EditRole from './EditRole.vue';
import SortByDropdown from './SortByDropdown.vue';
import timeMixin from 'dashboard/mixins/time';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';

export default {
  components: {
    EditRole,
    SortByDropdown,
  },
  mixins: [globalConfigMixin, timeMixin, hasPermissionMixin],
  data() {
    return {
      loading: {},
      showDeletePopup: false,
      showEditPopup: false,
      currentRole: {},
      sort: { key: null, direction: null },
      activeDropdown: null,
    };
  },
  computed: {
    ...mapGetters({
      rolesList: 'roles/getRoles',
      uiFlags: 'roles/getUIFlags',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
    }),
    deleteMessage() {
      return ` ${this.currentRole.name}?`;
    },
  },
  mounted() {
    this.$store.dispatch('roles/get');
    document.addEventListener('click', this.closeDropdown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeDropdown);
  },
  methods: {
    openEditPopup(role) {
      this.showEditPopup = true;
      this.currentRole = role;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    openDeletePopup(role) {
      if (role.is_system_role) return;
      this.showDeletePopup = true;
      this.currentRole = role;
    },
    closeDeletePopup() {
      this.showDeletePopup = false;
    },
    confirmDeletion() {
      this.loading[this.currentRole.id] = true;
      this.closeDeletePopup();
      this.deleteRole(this.currentRole.id);
    },
    toggleDropdown(key) {
      this.activeDropdown = this.activeDropdown === key ? null : key;
    },
    closeDropdown() {
      this.activeDropdown = null;
    },
    async onSort(key, direction) {
      if (key === this.sort.key && direction === this.sort.direction)
        this.sort = { key: null, direction: null };
      else this.sort = { key, direction };

      this.activeDropdown = null;
      await this.$store.dispatch('roles/get', {
        sortBy: this.sort.key,
        direction: this.sort.direction,
      });
    },
    isGeneralManager(name) {
      return name === 'General Manager';
    },
    async deleteRole(id) {
      try {
        await this.$store.dispatch('roles/delete', id);
        this.showAlert(this.$t('ROLES.DELETE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(error.message);
      } finally {
        this.loading[this.currentRole.id] = false;
        this.currentRole = {};
      }
    },
    showAlert(message) {
      bus.$emit('newToastMessage', message);
    },
  },
};
</script>
<style lang="scss" scoped>
table {
  th {
    @apply bg-neutral-200 text-[#717171] px-3 py-2.5 font-bold;

    .table-header {
      @apply flex flex-row justify-between items-center;
      span {
        letter-spacing: normal;
      }
    }

    &:last-child {
      @apply px-0;
    }
  }

  td {
    @apply px-3 py-2.5;

    &:last-child {
      @apply px-[9px];
    }
  }

  tr {
    @apply border-0;
  }
}
</style>
